/**
 * World Cup 2026 bracket engine (pure, no deps). Encodes the OFFICIAL FIFA 2026
 * knockout topology: 12 groups (A-L) → Round of 32 (matches 73-88) → R16 → QF →
 * SF → Final, plus the best-third allocation. Drives the bracket predictor: given
 * the user's group order + chosen thirds it builds the R32 ties, then advances
 * click-picked winners up the fixed winner-tree to the champion.
 *
 * Numbering follows the Wikipedia connectivity tree (internally consistent end
 * to end). The R16-onward winner-vs-winner tree is fixed regardless of who
 * qualifies; only the R32 third-placed inputs are dynamic.
 */

export type GroupKey = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L';
export const GROUP_KEYS: GroupKey[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

export type RoundKey = 'R32' | 'R16' | 'QF' | 'SF' | 'F';
export const ROUND_KEYS: RoundKey[] = ['R32', 'R16', 'QF', 'SF', 'F'];

/** A reference to a qualifier slot the R32 is built from. */
type SlotRef =
  | { kind: 'winner'; group: GroupKey }
  | { kind: 'runnerUp'; group: GroupKey }
  | { kind: 'third'; candidates: GroupKey[] };

/** Round of 32 ties (M73-M88), official group-position pairings. */
export const R32: { id: number; home: SlotRef; away: SlotRef }[] = [
  { id: 73, home: { kind: 'runnerUp', group: 'A' }, away: { kind: 'runnerUp', group: 'B' } },
  { id: 74, home: { kind: 'winner', group: 'E' }, away: { kind: 'third', candidates: ['A', 'B', 'C', 'D', 'F'] } },
  { id: 75, home: { kind: 'winner', group: 'F' }, away: { kind: 'runnerUp', group: 'C' } },
  { id: 76, home: { kind: 'winner', group: 'C' }, away: { kind: 'runnerUp', group: 'F' } },
  { id: 77, home: { kind: 'winner', group: 'I' }, away: { kind: 'third', candidates: ['C', 'D', 'F', 'G', 'H'] } },
  { id: 78, home: { kind: 'runnerUp', group: 'E' }, away: { kind: 'runnerUp', group: 'I' } },
  { id: 79, home: { kind: 'winner', group: 'A' }, away: { kind: 'third', candidates: ['C', 'E', 'F', 'H', 'I'] } },
  { id: 80, home: { kind: 'winner', group: 'L' }, away: { kind: 'third', candidates: ['E', 'H', 'I', 'J', 'K'] } },
  { id: 81, home: { kind: 'winner', group: 'D' }, away: { kind: 'third', candidates: ['B', 'E', 'F', 'I', 'J'] } },
  { id: 82, home: { kind: 'winner', group: 'G' }, away: { kind: 'third', candidates: ['A', 'E', 'H', 'I', 'J'] } },
  { id: 83, home: { kind: 'runnerUp', group: 'K' }, away: { kind: 'runnerUp', group: 'L' } },
  { id: 84, home: { kind: 'winner', group: 'H' }, away: { kind: 'runnerUp', group: 'J' } },
  { id: 85, home: { kind: 'winner', group: 'B' }, away: { kind: 'third', candidates: ['E', 'F', 'G', 'I', 'J'] } },
  { id: 86, home: { kind: 'winner', group: 'J' }, away: { kind: 'runnerUp', group: 'H' } },
  { id: 87, home: { kind: 'winner', group: 'K' }, away: { kind: 'third', candidates: ['D', 'E', 'I', 'J', 'L'] } },
  { id: 88, home: { kind: 'runnerUp', group: 'D' }, away: { kind: 'runnerUp', group: 'G' } },
];

/** Winner-tree: each later-round tie is fed by the winners of two earlier ones. */
export const WINNER_TREE: Record<number, [number, number]> = {
  // Round of 16 (M89-96)
  89: [74, 77], 90: [73, 75], 91: [76, 78], 92: [79, 80],
  93: [83, 84], 94: [81, 82], 95: [86, 88], 96: [85, 87],
  // Quarter-finals (M97-100)
  97: [89, 90], 98: [93, 94], 99: [91, 92], 100: [95, 96],
  // Semi-finals (M101-102)
  101: [97, 98], 102: [99, 100],
  // Final (M104)
  104: [101, 102],
};

/** Match ids per round, in bracket order. */
export const ROUND_MATCHES: Record<RoundKey, number[]> = {
  R32: R32.map((tie) => tie.id),
  R16: [89, 90, 91, 92, 93, 94, 95, 96],
  QF: [97, 98, 99, 100],
  SF: [101, 102],
  F: [104],
};

export interface Qualifiers {
  /** group letter → team id finishing 1st / 2nd / 3rd. */
  winner: Partial<Record<GroupKey, string>>;
  runnerUp: Partial<Record<GroupKey, string>>;
  third: Partial<Record<GroupKey, string>>;
}

/** team id → group letter, for the chosen thirds. */
export type ThirdGroupOf = (teamId: string) => GroupKey | undefined;

/**
 * Assign the 8 chosen third-placed teams to the 8 third-facing R32 slots,
 * respecting each slot's candidate-group set and never reusing a team - FIFA's
 * no-early-rematch rule. This is a bipartite matching, solved with Kuhn's
 * augmenting-path algorithm so that whenever a perfect/maximum assignment
 * exists it is found. A third that can't land in any COMPATIBLE slot leaves that
 * slot empty (null) - we never place a team in a slot whose candidate set
 * excludes its group. Returns slotMatchId → team id.
 */
export function allocateThirds(chosenThirdIds: string[], groupOf: ThirdGroupOf): Record<number, string> {
  const thirdSlots = R32.filter((tie) => tie.away.kind === 'third');
  // adjacency: third index → eligible slot indices (group letter in candidate set).
  const adj: number[][] = chosenThirdIds.map((id) => {
    const g = groupOf(id);
    return thirdSlots
      .map((slot, si) => (g && (slot.away as { candidates: GroupKey[] }).candidates.includes(g) ? si : -1))
      .filter((si) => si >= 0);
  });
  const slotToThird: number[] = thirdSlots.map(() => -1);
  const tryAssign = (thirdIdx: number, seen: boolean[]): boolean => {
    for (const si of adj[thirdIdx]) {
      if (seen[si]) continue;
      seen[si] = true;
      if (slotToThird[si] === -1 || tryAssign(slotToThird[si], seen)) {
        slotToThird[si] = thirdIdx;
        return true;
      }
    }
    return false;
  };
  // Most-constrained third (fewest eligible slots) first improves the matching.
  const thirdOrder = chosenThirdIds.map((_, i) => i).sort((a, b) => adj[a].length - adj[b].length);
  for (const thirdIdx of thirdOrder) {
    tryAssign(thirdIdx, thirdSlots.map(() => false));
  }
  const assignment: Record<number, string> = {};
  thirdSlots.forEach((slot, si) => {
    if (slotToThird[si] >= 0) assignment[slot.id] = chosenThirdIds[slotToThird[si]];
  });
  return assignment;
}

export interface R32Tie {
  id: number;
  home: string | null;
  away: string | null;
}

/** Build the 16 Round-of-32 ties (team ids) from qualifiers + third allocation. */
export function buildR32(qualifiers: Qualifiers, thirdBySlot: Record<number, string>): R32Tie[] {
  const resolve = (ref: SlotRef, slotId: number): string | null => {
    if (ref.kind === 'winner') return qualifiers.winner[ref.group] ?? null;
    if (ref.kind === 'runnerUp') return qualifiers.runnerUp[ref.group] ?? null;
    return thirdBySlot[slotId] ?? null;
  };
  return R32.map((tie) => ({ id: tie.id, home: resolve(tie.home, tie.id), away: resolve(tie.away, tie.id) }));
}

/**
 * Resolve every knockout tie's two competitors given the R32 seeding and the
 * user's per-match winner picks. Returns matchId → { home, away } (team ids or
 * null until both feeders are decided), for all rounds R32→Final.
 */
export function resolveTies(
  r32: R32Tie[],
  picks: Record<number, string | null>,
): Record<number, { home: string | null; away: string | null }> {
  const ties: Record<number, { home: string | null; away: string | null }> = {};
  for (const tie of r32) ties[tie.id] = { home: tie.home, away: tie.away };

  const winnerOf = (matchId: number): string | null => {
    const pick = picks[matchId];
    const tie = ties[matchId];
    if (!tie) return null;
    // A pick only counts if it's actually one of the two competitors.
    if (pick && (pick === tie.home || pick === tie.away)) return pick;
    return null;
  };

  for (const matchId of [89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 104]) {
    const [a, b] = WINNER_TREE[matchId];
    ties[matchId] = { home: winnerOf(a), away: winnerOf(b) };
  }
  return ties;
}

export const FINAL_MATCH_ID = 104;
export const CHAMPION_MATCH_ID = 104;
